import React from 'react';
import type { AppBootstrapOptions } from './types/AppBootstrapOptions';
import type { BootstrapOptions } from 'owa-bootstrap';
import { bootstrap } from 'owa-bootstrap/lib/bootstrap';
import { registerBootstrapOptions } from './optionsRegistry';
import AppModule from './components/AppModule';
import { initializeModule } from 'owa-app-module-store';
import { getServiceWorkerConfig } from './getServiceWorkerConfig';
import type { ModuleConfigMap } from 'owa-shared-start-types';
import { setModuleToConfigMap } from './changeModuleIfEnabledOrchestrator';
import { autoUpdateFavIcon } from './autoUpdateFavIcon';
import type { SessionData } from 'owa-service/lib/types/SessionData';
import type { MailboxInfo } from 'owa-client-types';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { initializeWin32StyleStore } from 'owa-theme-win32-style-store';

export type AppBootstrapFunction = (
    options: AppBootstrapOptions,
    configRegistry?: ModuleConfigMap<BootstrapOptions>
) => Promise<void>;

export const appBootstrap: AppBootstrapFunction = async (
    options: AppBootstrapOptions,
    configRegistry?: ModuleConfigMap<BootstrapOptions>
) => {
    if (configRegistry) {
        setModuleToConfigMap(configRegistry);
    }

    const mod = options.appModuleProps.module;
    initializeModule(mod);
    const serviceWorkerConfig = getServiceWorkerConfig(mod);
    if (serviceWorkerConfig) {
        options.swConfig = serviceWorkerConfig;
    }

    registerBootstrapOptions(options);

    const suiteWrapper = options.strategies?.suiteWrapper;
    const components = suiteWrapper ? await suiteWrapper.import().then(i => i()) : undefined;
    let wrapSuiteHeader:
        | ((
              suiteHeader: React.ReactNode,
              mecontrolLauncher?: React.FC<{
                  mailboxInfo: MailboxInfo;
              }>
          ) => React.ReactNode)
        | undefined;
    if (components) {
        options.windowIcons = components.windowIcons;
        wrapSuiteHeader = (suiteHeader, mecontrolLauncher) => (
            <components.suiteHeader
                suiteHeader={suiteHeader}
                mecontrolLauncher={mecontrolLauncher}
            />
        );
    }

    options.renderMainComponent = () => <AppModule wrapSuiteHeader={wrapSuiteHeader} />;

    return bootstrap({
        ...options,
        initializeState: (sessionData?: SessionData) => {
            if (isGlobalFeatureEnabled('mon-win32-styling')) {
                initializeWin32StyleStore();
            }

            // Auto update fav icon in initializeState to make sure
            // feature flags are initialized
            autoUpdateFavIcon();
            return options.initializeState?.(sessionData);
        },
    });
};
